import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { AuditLogDetailSheet } from "@/features/audit-logs/components/AuditLogDetailSheet";
import { AuditLogsFilters } from "@/features/audit-logs/components/AuditLogsFilters";
import { AuditLogsTable } from "@/features/audit-logs/components/AuditLogsTable";
import { auditLogService } from "@/features/audit-logs/services/auditLogService";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { useScopedDepartments } from "@/features/reports/hooks/useReportScope";
import { normalizeReportDateFilters } from "@/features/reports/lib/report-query-utils";
import { ReportSectionError } from "@/features/reports/components/ReportSectionError";
import { ReportTableSkeleton } from "@/features/reports/components/ReportTableSkeleton";
import { handleApiError } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type { AuditLog } from "@/types/audit-log";
import type { ListAuditLogsQuery } from "@/types/audit-log";

const PAGE_SIZE = 25;

export default function AuditLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const action = searchParams.get("action") ?? undefined;
  const userId = searchParams.get("userId") ?? undefined;
  const departmentId = searchParams.get("departmentId") ?? undefined;
  const page = Math.max(0, Number(searchParams.get("page") ?? "1") - 1);

  const patchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data: deptData } = useDepartments({ limit: 100 });
  const allDepartments = useMemo(() => deptData?.items ?? [], [deptData]);
  const departments = useScopedDepartments(allDepartments);

  const departmentLookup = useMemo(
    () =>
      Object.fromEntries(
        allDepartments.map((d) => [d.id, d.nameAr ?? d.name]),
      ),
    [allDepartments],
  );

  const reportWindow = useMemo(
    () => normalizeReportDateFilters(from, to),
    [from, to],
  );

  const filters = useMemo<ListAuditLogsQuery>(
    () => ({
      ...reportWindow,
      action,
      userId,
      departmentId,
      page: page + 1,
      pageSize: PAGE_SIZE,
    }),
    [reportWindow, action, userId, departmentId, page],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.auditLogs.list(filters),
    queryFn: () => auditLogService.list(filters),
  });

  const logs = data?.items ?? [];
  const pageCount = Math.max(1, data?.meta.totalPages ?? 1);

  const hasActiveFilters = Boolean(from || to || action || userId || departmentId);

  const setFilter = (updates: Record<string, string | undefined>) => {
    patchParams({ ...updates, page: undefined });
  };

  const clearFilters = () => {
    patchParams({
      from: undefined,
      to: undefined,
      action: undefined,
      userId: undefined,
      departmentId: undefined,
      page: undefined,
    });
  };

  useEffect(() => {
    if (page > 0 && page >= pageCount) {
      patchParams({ page: String(pageCount) });
    }
  }, [page, pageCount, patchParams]);

  async function onExport() {
    setIsExporting(true);
    try {
      await auditLogService.exportCsv({
        ...reportWindow,
        action,
        userId,
        departmentId,
      });
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير سجل التدقيق" });
    } finally {
      setIsExporting(false);
    }
  }

  function onViewDetail(log: AuditLog) {
    setSelectedLog(log);
    setDetailOpen(true);
  }

  return (
    <AdminPageShell
      title="سجل التدقيق"
      description="تتبع جميع الإجراءات الإدارية على النظام"
      isLoading={isLoading && !data}
      error={error}
      actions={
        <Button variant="outline" onClick={onExport} disabled={isExporting}>
          <Download className="ml-2 size-4" />
          {isExporting ? "جاري التصدير..." : "تصدير CSV"}
        </Button>
      }
    >
      <div className="space-y-4">
        <AuditLogsFilters
          from={from}
          to={to}
          action={action}
          userId={userId}
          departmentId={departmentId}
          departments={departments}
          onFromChange={(v) => setFilter({ from: v || undefined })}
          onToChange={(v) => setFilter({ to: v || undefined })}
          onActionChange={(v) => setFilter({ action: v })}
          onUserIdChange={(v) => setFilter({ userId: v })}
          onDepartmentChange={(v) => setFilter({ departmentId: v })}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
        />

        {error ? (
          <ReportSectionError onRetry={() => refetch()} />
        ) : isLoading && !data ? (
          <ReportTableSkeleton columns={7} />
        ) : (
          <AuditLogsTable
            logs={logs}
            departmentLookup={departmentLookup}
            pageIndex={page}
            pageCount={pageCount}
            onPageChange={(p) => patchParams({ page: p === 0 ? undefined : String(p + 1) })}
            onViewDetail={onViewDetail}
          />
        )}
      </div>

      <AuditLogDetailSheet
        logId={selectedLog?.id ?? null}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        departmentLookup={departmentLookup}
        departmentName={
          selectedLog?.departmentId
            ? departmentLookup[selectedLog.departmentId]
            : undefined
        }
        preview={selectedLog ?? undefined}
      />
    </AdminPageShell>
  );
}
